import React from "react";
import { X, MessageCircle } from "lucide-react";
import { PromptInputBox } from "@/components/ui/ai-prompt-box";
import { useDocumentChat } from "@/context/DocumentChatContext";
import { ChatBubble } from "./ChatBubble";
import { useParams } from "react-router-dom";
import { useRouteIsDocument } from "@/hooks/useRouteIsDocument";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentPermissions } from "@/hooks/useDocumentPermissions";

const LS_KEY = "docChatInside:minimized";

export default function DocumentChatInside() {
  const showChat = useRouteIsDocument();
  const { documentId } = useParams<{ documentId: string }>();
  const { messages, sendMessage, isLoading, loadMessages } = useDocumentChat();
  const { canSendMessage, planStatus } = useDocumentPermissions();
  const chatRef = React.useRef<HTMLDivElement>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  
  const isBlocked = !canSendMessage || planStatus === 'free';

  const [minimized, setMinimized] = React.useState<boolean>(() => {
    return localStorage.getItem(LS_KEY) === "true";
  });

  // Constantes responsivas
  const MAX_W = 384, MIN_W = 280, GUTTER = 16;
  
  // Fonte única de verdade para posição e largura
  const [dockLeft, setDockLeft] = React.useState(GUTTER);
  const [chatWidth, setChatWidth] = React.useState(MAX_W);
  const [compact, setCompact] = React.useState(false);
  const lastChatWidthRef = React.useRef<number>(MAX_W);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  React.useEffect(() => {
    localStorage.setItem(LS_KEY, minimized ? "true" : "false");
  }, [minimized]);

  // Dynamic positioning and responsive logic
  const updateDock = React.useCallback(() => {
    const sidebar = document.querySelector('[data-sidebar="sidebar"]') as HTMLElement | null;
    const paper = document.querySelector('[data-doc-anchor="paper"]') as HTMLElement || 
                 document.querySelector('.document-editor-layout .max-w-4xl.mx-auto.bg-white') as HTMLElement | null;
    
    const viewportW = window.innerWidth;
    
    if (!paper) {
      const left = GUTTER;
      setChatWidth(MAX_W);
      setDockLeft(left);
      setCompact(false);
      lastChatWidthRef.current = MAX_W;
      if (chatRef.current) chatRef.current.style.left = `${left}px`;
      return;
    }
    
    const paperRect = paper.getBoundingClientRect();
    let gapWidth = 0;
    let gapLeft = GUTTER;
    
    if (sidebar) {
      const sbRect = sidebar.getBoundingClientRect();
      gapLeft = sbRect.right;
      gapWidth = Math.max(0, paperRect.left - gapLeft);
    } else {
      gapWidth = Math.max(0, paperRect.left - GUTTER);
    }
    
    // Calcular largura responsiva
    const maxFitWidth = Math.max(0, gapWidth - GUTTER);
    let chatW = Math.min(MAX_W, Math.max(MIN_W, maxFitWidth));
    const isCompact = maxFitWidth < MIN_W;
    
    // Se modo compacto, usar largura mínima para cálculo de posição
    if (isCompact) {
      chatW = MIN_W;
    }
    
    // Posicionamento
    let idealLeft: number;
    
    if (sidebar && !isCompact) {
      // Centraliza no gap se couber, senão encosta no papel
      if (gapWidth >= chatW + GUTTER) {
        idealLeft = gapLeft + (gapWidth - chatW) / 2;
      } else {
        idealLeft = paperRect.left - chatW - GUTTER;
      }
    } else {
      // Encosta no papel com margem
      idealLeft = paperRect.left - chatW - GUTTER;
    }
    
    // Clamps para não sair da tela
    idealLeft = Math.max(GUTTER, Math.min(idealLeft, viewportW - chatW - GUTTER));
    
    // Atualizar estados
    setChatWidth(Math.round(chatW));
    setDockLeft(Math.round(idealLeft));
    setCompact(isCompact);
    lastChatWidthRef.current = chatW;
    
    // Aplicar posição no elemento se existir
    if (chatRef.current) {
      chatRef.current.style.left = `${Math.round(idealLeft)}px`;
    }
  }, [MAX_W, MIN_W, GUTTER]);

  React.useEffect(() => {
    if (!showChat) return;
    
    updateDock();
    
    const ro = new ResizeObserver(updateDock);
    const paper = document.querySelector('[data-doc-anchor="paper"]') || 
                 document.querySelector('.document-editor-layout .max-w-4xl.mx-auto.bg-white');
    const sidebar = document.querySelector('[data-sidebar="sidebar"]');
    
    if (paper) ro.observe(paper as Element);
    if (sidebar) ro.observe(sidebar as Element);
    
    // Anti-jitter with requestAnimationFrame
    let raf: number | null = null;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateDock);
    };
    
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    
    const mo = new MutationObserver(updateDock);
    if (sidebar) mo.observe(sidebar, { attributes: true, attributeFilter: ['style', 'class', 'data-collapsed'] });
    
    return () => {
      ro.disconnect();
      mo.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule);
    };
  }, [updateDock, showChat]);

  // Force recalculation when chat is reopened
  React.useEffect(() => {
    if (!minimized && showChat) {
      // Delay to ensure DOM is stable
      setTimeout(() => {
        updateDock();
      }, 100);
    }
  }, [minimized, showChat, updateDock]);

  // Load messages when documentId changes
  React.useEffect(() => {
    if (documentId && showChat) {
      loadMessages(documentId);
    }
  }, [documentId, loadMessages, showChat]);

  // 🚀 LISTENER para geração inicial de documento
  React.useEffect(() => {
    if (!documentId || !showChat) return;

    const handleInitialMessage = async (event: CustomEvent) => {
      console.log('📝 DocumentChatInside: Evento initialDocumentMessage capturado:', event.detail);
      const { message, files, isBlankDocument } = event.detail;
      
      if (isBlankDocument && documentId) {
        console.log('📝 DocumentChatInside: Iniciando geração para documento:', documentId);
        await generateInitialDocument(message, files);
      }
    };

    window.addEventListener('initialDocumentMessage', handleInitialMessage as EventListener);
    
    return () => {
      window.removeEventListener('initialDocumentMessage', handleInitialMessage as EventListener);
    };
  }, [documentId, showChat]);

  // 🔥 Função para gerar documento inicial via edge function
  const generateInitialDocument = async (prompt: string, files?: File[]) => {
    if (!documentId) return;

    try {
      console.log('📝 DocumentChatInside: Chamando edge function generate-document com prompt:', prompt.substring(0, 100) + '...');
      
      const { data, error } = await supabase.functions.invoke('generate-document', {
        body: { 
          prompt, 
          documentId, 
          documentType: 'DOCUMENTO_CHAT',
          files: files || []
        }
      });

      if (error) {
        console.error('❌ DocumentChatInside: Erro na edge function:', error);
        return;
      }

      if (data?.content) {
        console.log('✅ DocumentChatInside: Conteúdo gerado, aplicando ao editor via evento...');
        
        // Disparar evento para aplicar conteúdo no editor
        window.dispatchEvent(new CustomEvent('applyAIContentToEditor', {
          detail: { content: data.content, documentId }
        }));
      }
    } catch (error) {
      console.error('❌ DocumentChatInside: Erro inesperado na geração:', error);
    }
  };

  // Auto scroll to bottom when messages change
  React.useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSendMessage = async (message: string, files?: File[]) => {
    if (documentId) {
      await sendMessage(message, documentId);
    }
  };

  // Don't render if not on document routes
  if (!showChat) return null;

  if (minimized || compact) {
    return (
      <button
        aria-label="Abrir chat do documento"
        onClick={() => setMinimized(false)}
        style={{ left: dockLeft }}
        className="fixed bottom-4 z-[999] flex items-center gap-2 rounded-full px-3 py-2 shadow-lg print:hidden
                   bg-white/80 backdrop-blur border border-white/20 hover:scale-105 transition text-sm"
      >
        <MessageCircle className="size-4" />
        Chat do Documento
      </button>
    );
  }

  return (
    <div
      ref={chatRef}
      className="fixed bottom-0 z-[999] print:hidden"
      data-doc-chat
    >
      <div 
        style={{ width: chatWidth }}
        className="backdrop-blur-md bg-white/10 shadow-xl rounded-xl flex flex-col h-[min(48rem,calc(100vh-88px))] max-h-[calc(100vh-88px)] p-4"
      >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
            <span className="text-xs font-medium">Chat do Documento</span>
            <button
              aria-label="Minimizar chat"
              onClick={() => setMinimized(true)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Message History */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {messages.map((m) => (
              <ChatBubble key={m.id} role={m.role as "assistant" | "user"}>
                {m.content}
              </ChatBubble>
            ))}
            {isLoading && <ChatBubble role="assistant">Digitando…</ChatBubble>}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-2 border-t border-gray-200">
            <PromptInputBox
              onSend={handleSendMessage}
              placeholder={isBlocked ? "Chat bloqueado no plano gratuito - Faça upgrade para usar" : "Estou aqui para ajudar..."}
              disabled={isBlocked}
            />
          </div>
        </div>
    </div>
  );
}